import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';  
import logo from '../components/logo.png';
import { Settings, Languages } from 'lucide-react';
import Notifications from '../components/Notifications'; 
import '../styles/DashboardNavbar.css';

function DashboardNavbar() {
  const navigate = useNavigate();
  const { workspaceNames, selectedWorkspace, setSelectedWorkspace, authUserId } = useWorkspace();
  const { language, toggleLanguage } = useLanguage();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openProfileDropdown, setOpenProfileDropdown] = useState(false);
  const [openPeopleFinancials, setOpenPeopleFinancials] = useState(false);
  const [openGrants, setOpenGrants] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [openWorkspaceDropdown, setOpenWorkspaceDropdown] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const profileDropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const peopleFinancialsRef = useRef(null);
  const grantsRef = useRef(null);

  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const toggleProfileDropdown = () => {
    setOpenProfileDropdown(!openProfileDropdown);
  };

  const togglePeopleFinancials = () => {
    setOpenPeopleFinancials(!openPeopleFinancials);
    setOpenGrants(false);
  };

  const toggleGrants = () => {
    setOpenGrants(!openGrants);
    setOpenPeopleFinancials(false);
  };

  const handleWorkspaceChange = (event) => {
    setSelectedWorkspace(event.target.value);
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      
      navigate('/', { replace: true });

      window.history.replaceState(null, '', '/');
    } catch (error) {
      console.error('Error signing out:', error.message);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current && 
        !profileDropdownRef.current.contains(event.target) && 
        !profileButtonRef.current.contains(event.target)
      ) {
        setOpenProfileDropdown(false);
      }
      if (
        peopleFinancialsRef.current && 
        !peopleFinancialsRef.current.contains(event.target)
      ) {
        setOpenPeopleFinancials(false);
      }
      if (
        grantsRef.current && 
        !grantsRef.current.contains(event.target)
      ) {
        setOpenGrants(false);
      }
      if (
        !event.target.closest('.workspace-selector-container') && 
        !event.target.closest('.workspace-selector-button')
      ) {
        setOpenWorkspaceDropdown(false);
      }
      if (
        !event.target.closest('.notifications-container') && 
        !event.target.closest('.notifications-button')
      ) {
        const event = new CustomEvent('closeNotifications');
        window.dispatchEvent(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!authUserId) return;

      // First check user_profiles table
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('system_role')
        .eq('id', authUserId)
        .single();

      if (profile?.system_role?.startsWith('org_')) {
        setHasAdminAccess(true);
        return;
      }

      // If not org_, check user_workspace_roles
      const { data: workspaceRole } = await supabase
        .from('user_workspace_roles')
        .select('workspace_role')
        .eq('user_id', authUserId)
        .eq('workspace_role', 'ws_admin')
        .single();

      setHasAdminAccess(!!workspaceRole);
    };

    checkAdminAccess();
  }, [authUserId]);

  return (
    <nav className="dashboard-navbar">
      <div className="nav-inner-wrapper">
        <img src={logo} alt="Logo" className="logo" onClick={() => navigate('/dashboard')} />
        <button className="mobile-menu-button" onClick={toggleMenu}>
          ☰
        </button>
        <div className={`nav-buttons ${isMenuOpen ? 'active' : ''}`}>
          <button className="nav-button" onClick={() => {
            navigate('/dashboard');
            setIsMenuOpen(false);
          }}>
            {language === 'en' ? 'Home' : 'Startseite'}
          </button>
          <button className="nav-button" onClick={() => {
            navigate('/documents');
            setIsMenuOpen(false);
          }}>
            {language === 'en' ? 'Documents' : 'Dokumente'}
          </button>
        </div>
        <div className="right-section">
          {workspaceNames.length > 1 && (
            <div className="workspace-selector-container" style={{ position: 'relative' }}>
              <label className="workspace-label"></label>
              <button 
                className="workspace-selector-button"
                onClick={() => setOpenWorkspaceDropdown(!openWorkspaceDropdown)}
              >
                {selectedWorkspace}
              </button>
              {openWorkspaceDropdown && (
                <div className="workspace-dropdown-menu">
                  {workspaceNames
                    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                    .map((name, index) => (
                      <div
                        key={index}
                        className="workspace-dropdown-item"
                        onClick={() => {
                          setSelectedWorkspace(name);
                          setOpenWorkspaceDropdown(false);
                        }}
                      >
                        {name}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
          <button 
            className="language-button"
            onClick={toggleLanguage}
            title={language === 'en' ? 'Switch to German' : 'Zu Englisch wechseln'}
          >
            <Languages size={20} />
            <span className="language-text">{language === 'en' ? 'DE' : 'EN'}</span>
          </button>
          <Notifications />
          <div className="profile-dropdown" ref={profileDropdownRef}>
            <button className="profile-button" ref={profileButtonRef} onClick={toggleProfileDropdown}>
              <Settings fill="none" stroke="currentColor"/>
            </button>
            {openProfileDropdown && (
              <div className="profile-dropdown-menu">
                <button className="profile-dropdown-item" onClick={handleSignOut}>
                  {language === 'en' ? 'Sign out' : 'Abmelden'}
                </button>
                <button className="profile-dropdown-item" onClick={() => {
                  navigate('/profile');
                  setIsMenuOpen(false);
                }}>
                  {language === 'en' ? 'My Profile' : 'Mein Profil'}
                </button>
                {hasAdminAccess && (
                  <>
                    <button className="profile-dropdown-item" onClick={() => {
                      navigate('/profile');
                      setTimeout(() => {
                        const event = new CustomEvent('profileSectionChange', { detail: 'companyProfile' });
                        window.dispatchEvent(event);
                      }, 100);
                      setIsMenuOpen(false);
                    }}>
                      {language === 'en' ? 'Company Settings' : 'Unternehmenseinstellungen'}
                    </button>
                    <button className="profile-dropdown-item" onClick={() => {
                      navigate('/profile');
                      setTimeout(() => {
                        const event = new CustomEvent('profileSectionChange', { detail: 'team' });
                        window.dispatchEvent(event);
                      }, 100);
                      setIsMenuOpen(false);
                    }}>
                      {language === 'en' ? 'Team Settings' : 'Teameinstellungen'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default DashboardNavbar;
